import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import catalog from '../../catalog/Catalog.module.css';
import { formatMoney, parseNumber } from '../invoiceForm';
import {
  createCashDayAdjustment,
  deleteCashDayAdjustment,
  getCashDay,
  upsertCashDay,
} from '../services/invoiceService';
import { CashAdjustmentType, CashDay } from '../types/Invoice';
import styles from '../Invoice.module.css';

type InvoiceCashDayPanelProps = {
  date: string;
  locationId?: string;
  currency?: string;
};

const adjustmentTypes: CashAdjustmentType[] = ['pettyCash', 'cashTopUp', 'bankedCash', 'variance'];

const formatSigned = (value: number, currency?: string) => {
  const formatted = formatMoney(Math.abs(value), currency);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const moneyEquals = (left: number, right: number) => Math.abs(left - right) < 0.005;

const parseMoney = (value: string) => {
  const parsed = parseNumber(value);
  return parsed === undefined || parsed < 0 ? undefined : parsed;
};

export const InvoiceCashDayPanel: React.FC<InvoiceCashDayPanelProps> = ({ date, locationId, currency: fallbackCurrency }) => {
  const { t } = useTranslation(['invoices', 'common']);
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(PermissionScopes.invoicesUpdate);
  const [item, setItem] = React.useState<CashDay | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [openingCash, setOpeningCash] = React.useState('');
  const [closingCash, setClosingCash] = React.useState('');
  const [terminalCard, setTerminalCard] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [adjustmentType, setAdjustmentType] = React.useState<CashAdjustmentType>('pettyCash');
  const [adjustmentAmount, setAdjustmentAmount] = React.useState('');
  const [adjustmentNote, setAdjustmentNote] = React.useState('');

  const apply = React.useCallback((next: CashDay) => {
    setItem(next);
    setOpeningCash(next.openingCash.toFixed(2));
    setClosingCash(next.closingCash === undefined ? '' : next.closingCash.toFixed(2));
    setTerminalCard(next.terminalCardPayments.toFixed(2));
    setNotes(next.notes || '');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCashDay(date, locationId)
      .then(next => {
        if (!cancelled) {
          apply(next);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('failedToLoadCashDay', { ns: 'invoices' }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apply, date, locationId, t]);

  const run = async (work: () => Promise<CashDay>) => {
    setSaving(true);
    setError(null);
    try {
      apply(await work());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('failedToSaveCashDay', { ns: 'invoices' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className={styles.hint}>{t('loadingCashDay', { ns: 'invoices' })}</p>;
  if (!item) return error ? <div className="error">{error}</div> : null;

  const currency = item.currency || fallbackCurrency;
  const locked = !canUpdate || item.status === 'finalized';
  const parsedOpening = parseMoney(openingCash);
  const parsedClosing = closingCash.trim() === '' ? undefined : parseMoney(closingCash);
  const parsedTerminal = parseMoney(terminalCard);
  const parsedAdjustment = parseNumber(adjustmentAmount);
  const openingDirty = parsedOpening !== undefined && !moneyEquals(parsedOpening, item.openingCash);
  const closingDirty = parsedClosing !== undefined
    && (item.closingCash === undefined || !moneyEquals(parsedClosing, item.closingCash));
  const terminalDirty = parsedTerminal !== undefined && !moneyEquals(parsedTerminal, item.terminalCardPayments);
  const notesDirty = notes !== (item.notes || '');
  const previousAlreadyApplied = item.previousClosingCash !== undefined
    && moneyEquals(item.openingCash, item.previousClosingCash);
  const expectedCash = parsedOpening === undefined
    ? item.expectedCash
    : item.expectedCash + (parsedOpening - item.openingCash);
  const cashDifference = parsedClosing === undefined ? undefined : parsedClosing - expectedCash;
  const cardDifference = parsedTerminal === undefined ? item.cardDifference : item.payments.card - parsedTerminal;
  const cashOff = cashDifference !== undefined && !moneyEquals(cashDifference, 0);
  const cardOff = !moneyEquals(cardDifference, 0);

  return (
    <section className={styles.cashDay}>
      <div className={styles.cashDayHead}>
        <h3>{t('cashDayTitle', { ns: 'invoices' })}</h3>
        <span className={`${catalog.statusBadge} ${item.status === 'finalized' ? styles.statusIssued : styles.statusDraft}`}>
          {t(item.status === 'finalized' ? 'cashDayFinalized' : 'cashDayOpen', { ns: 'invoices' })}
        </span>
      </div>
      {error ? <div className="error">{error}</div> : null}

      <div className={styles.cashDayGrid}>
        <div className={styles.summaryStrip}>
          <div className={styles.summaryStripHead}>
            <h3>{t('cashFlow', { ns: 'invoices' })}</h3>
          </div>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('openingCash', { ns: 'invoices' })}</label>
            <div className={styles.cashDayRow}>
              <input className={catalog.input} inputMode="decimal" value={openingCash} disabled={locked || saving} onChange={event => setOpeningCash(event.target.value)} />
              {canUpdate && item.status === 'draft' ? (
                <>
                  {item.previousClosingCash !== undefined ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      disabled={saving || previousAlreadyApplied}
                      onClick={() => void run(() => upsertCashDay({ date, locationId, copyPreviousClosing: true }))}
                    >
                      {t('usePreviousClosing', { ns: 'invoices' })}
                    </button>
                  ) : null}
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={saving || !openingDirty}
                    onClick={() => {
                      if (parsedOpening === undefined) return;
                      void run(() => upsertCashDay({ date, locationId, openingCash: parsedOpening }));
                    }}
                  >
                    {t('save', { ns: 'invoices' })}
                  </button>
                </>
              ) : null}
            </div>
            {item.previousClosingCash !== undefined ? (
              <p className={styles.hint}>{t('previousClosing', { ns: 'invoices' })}: {formatMoney(item.previousClosingCash, currency)}</p>
            ) : null}
          </div>

          <div className={catalog.field}>
            <label className={catalog.label}>{t('closingCash', { ns: 'invoices' })}</label>
            <div className={styles.cashDayRow}>
              <input className={catalog.input} inputMode="decimal" value={closingCash} disabled={locked || saving} onChange={event => setClosingCash(event.target.value)} />
              {canUpdate && item.status === 'draft' ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={saving || !closingDirty}
                    onClick={() => {
                      if (parsedClosing === undefined) return;
                      void run(() => upsertCashDay({ date, locationId, closingCash: parsedClosing }));
                    }}
                  >
                  {t('save', { ns: 'invoices' })}
                </button>
              ) : null}
            </div>
          </div>

          <div className={catalog.field}>
            <label className={catalog.label}>{t('cashAdjustments', { ns: 'invoices' })}</label>
            <p className={styles.hint}>{t('cashAdjustmentHint', { ns: 'invoices' })}</p>
            {item.adjustments.length === 0 ? <p className={styles.hint}>{t('noCashAdjustments', { ns: 'invoices' })}</p> : (
              <div className={styles.payments}>
                {item.adjustments.map(adjustment => (
                  <div key={adjustment.id} className={styles.paymentRow}>
                    <span>{t(`adjustment${adjustment.type.charAt(0).toUpperCase()}${adjustment.type.slice(1)}`, { ns: 'invoices' })}</span>
                    <span>{formatSigned(adjustment.amount, currency)}</span>
                    <span className={catalog.secondaryText}>{adjustment.note || '-'}</span>
                    {canUpdate && item.status === 'draft' ? (
                      <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => void run(() => deleteCashDayAdjustment(adjustment.id))}>
                        {t('deletePayment', { ns: 'invoices' })}
                      </button>
                    ) : <span />}
                  </div>
                ))}
              </div>
            )}
            {canUpdate && item.status === 'draft' ? (
              <div className={styles.cashDayRow}>
                <select className={catalog.input} value={adjustmentType} disabled={saving} onChange={event => setAdjustmentType(event.target.value as CashAdjustmentType)}>
                  {adjustmentTypes.map(type => (
                    <option key={type} value={type}>{t(`adjustment${type.charAt(0).toUpperCase()}${type.slice(1)}`, { ns: 'invoices' })}</option>
                  ))}
                </select>
                <input className={catalog.input} inputMode="decimal" placeholder={t('amount', { ns: 'invoices' })} value={adjustmentAmount} disabled={saving} onChange={event => setAdjustmentAmount(event.target.value)} />
                <input className={catalog.input} placeholder={t('note', { ns: 'invoices' })} value={adjustmentNote} disabled={saving} onChange={event => setAdjustmentNote(event.target.value)} />
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={saving || parsedAdjustment === undefined || parsedAdjustment === 0}
                  onClick={() => {
                    const parsed = parsedAdjustment;
                    if (!parsed) return;
                    let amount = parsed;
                    if ((adjustmentType === 'pettyCash' || adjustmentType === 'bankedCash') && amount > 0) amount = -amount;
                    if (adjustmentType === 'cashTopUp' && amount < 0) amount = Math.abs(amount);
                    void run(async () => {
                      const next = await createCashDayAdjustment({
                        date,
                        locationId,
                        type: adjustmentType,
                        amount,
                        note: adjustmentNote.trim() || undefined,
                      });
                      setAdjustmentAmount('');
                      setAdjustmentNote('');
                      return next;
                    });
                  }}
                >
                  {t('addAdjustment', { ns: 'invoices' })}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.summaryStrip}>
          <div className={styles.summaryStripHead}>
            <h3>{t('paymentReconciliation', { ns: 'invoices' })}</h3>
          </div>
          <dl className={styles.cashReconcile}>
            <div className={styles.summaryRow}>
              <dt>{t('openingCash', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(parsedOpening ?? item.openingCash, currency)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('cashIn', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(item.payments.cash, currency)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('cashAdjustments', { ns: 'invoices' })}</dt>
              <dd>{formatSigned(item.adjustmentTotal, currency)}</dd>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryRowStrong}`}>
              <dt>{t('expectedCash', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(expectedCash, currency)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('closingCash', { ns: 'invoices' })}</dt>
              <dd>{parsedClosing === undefined ? '—' : formatMoney(parsedClosing, currency)}</dd>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryRowStrong}`}>
              <dt>{t('cashDifference', { ns: 'invoices' })}</dt>
              <dd className={cashOff ? styles.warningHint : undefined}>
                {cashDifference === undefined ? '—' : formatSigned(cashDifference, currency)}
              </dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('methodCard', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(item.payments.card, currency)}</dd>
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('terminalCardPayments', { ns: 'invoices' })}</label>
              <div className={styles.cashDayRow}>
                <input className={catalog.input} inputMode="decimal" value={terminalCard} disabled={locked || saving} onChange={event => setTerminalCard(event.target.value)} />
                {canUpdate && item.status === 'draft' ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={saving || !terminalDirty}
                    onClick={() => {
                      if (parsedTerminal === undefined) return;
                      void run(() => upsertCashDay({ date, locationId, terminalCardPayments: parsedTerminal }));
                    }}
                  >
                    {t('save', { ns: 'invoices' })}
                  </button>
                ) : null}
              </div>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('cardDifference', { ns: 'invoices' })}</dt>
              <dd className={cardOff ? styles.warningHint : undefined}>
                {formatSigned(cardDifference, currency)}
              </dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('methodBank', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(item.payments.bank, currency)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('methodOther', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(item.payments.other, currency)}</dd>
            </div>
          </dl>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('cashDayNotes', { ns: 'invoices' })}</label>
            <textarea className={catalog.textarea} value={notes} disabled={locked || saving} onChange={event => setNotes(event.target.value)} />
            {canUpdate && item.status === 'draft' ? (
              <button className="btn btn-secondary" type="button" disabled={saving || !notesDirty} onClick={() => void run(() => upsertCashDay({ date, locationId, notes }))}>
                {t('save', { ns: 'invoices' })}
              </button>
            ) : null}
          </div>
          {canUpdate ? (
            <div className={styles.cashDayActions}>
              {item.status === 'draft' ? (
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={saving || parsedClosing === undefined}
                  onClick={() => {
                    if (parsedClosing === undefined) {
                      setError(t('closingCashRequired', { ns: 'invoices' }));
                      return;
                    }
                    const message = cashOff || cardOff
                      ? t('confirmCompleteCashDayWithDifference', { ns: 'invoices' })
                      : t('confirmCompleteCashDay', { ns: 'invoices' });
                    if (!window.confirm(message)) return;
                    void run(() => upsertCashDay({
                      date,
                      locationId,
                      openingCash: parsedOpening,
                      closingCash: parsedClosing,
                      terminalCardPayments: parsedTerminal,
                      notes,
                      status: 'finalized',
                    }));
                  }}
                >
                  {t('completeCashDay', { ns: 'invoices' })}
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    if (!window.confirm(t('confirmReopenCashDay', { ns: 'invoices' }))) return;
                    void run(() => upsertCashDay({ date, locationId, status: 'draft' }));
                  }}
                >
                  {t('reopenCashDay', { ns: 'invoices' })}
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
